import { useSnackbar } from '@robocorp/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useDispatch } from '~/hooks';
import { AssetCreateInput } from '~/schemas/assetStorageSchemas';
import { assetStorageService } from '~/services/assetStorageAPI';
import { UpdateAssetInput } from '~/services/assetStorageAPI/assetStorageService';
import queryClient from '~/queryClient';
import {
  UseApiMutationOptions,
  UseApiQueryOptions,
  formatErrorPayload,
  validateApiResponse,
} from './utils';
import { useCurrentWorkspaceQueryKey } from './utils/useQueryKey';

const ASSET_STORAGE_ROOT_KEY = ['asset_storage'];
const getAssetQueryKey = (assetId: string) => [...ASSET_STORAGE_ROOT_KEY, assetId];

export const ASSET_STORAGE_UPLOADS_ROOT_KEY = [...ASSET_STORAGE_ROOT_KEY, 'uploads'];

export const useListAssetsQuery = (
  options?: UseApiQueryOptions<typeof assetStorageService.listAssets>,
) => {
  const dispatch = useDispatch();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    queryKeyFormatter(ASSET_STORAGE_ROOT_KEY),
    async () => validateApiResponse(await assetStorageService.listAssets({ workspaceId })).data,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useGetAssetQuery = (
  assetId: string,
  options?: UseApiQueryOptions<typeof assetStorageService.getAsset>,
) => {
  const dispatch = useDispatch();
  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();

  return useQuery(
    queryKeyFormatter(getAssetQueryKey(assetId)),
    async () =>
      validateApiResponse(await assetStorageService.getAsset({ workspaceId, assetId })).data,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useDownloadAssetQuery = (
  options?: UseApiMutationOptions<typeof assetStorageService.getAsset>,
) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { workspaceId, queryKeyFormatter } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async ({ assetId }: { assetId: string }) => {
      const asset = validateApiResponse(
        await assetStorageService.getAsset({ workspaceId, assetId }),
      ).data;
      queryClient.setQueryData(queryKeyFormatter(getAssetQueryKey(assetId)), asset);

      if (asset.downloadUrl) {
        window.open(asset.downloadUrl);
      }

      return asset;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

export const useDeleteAssetMutation = (
  options?: UseApiMutationOptions<typeof assetStorageService.deleteAsset>,
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { workspaceId, queryKeyFormatter } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (assetId: string) => {
      const response = validateApiResponse(
        await assetStorageService.deleteAsset({ workspaceId, assetId }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      onSuccess: () => {
        addSnackbar({ message: t('assetStorage.assetDeleted'), variant: 'success' });
        queryClient.invalidateQueries(queryKeyFormatter(ASSET_STORAGE_ROOT_KEY));
      },
      ...options,
    },
  );
};

export const useBulkDeleteAssetMutation = (
  options?: UseApiMutationOptions<typeof assetStorageService.deleteAsset>,
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { workspaceId, queryKeyFormatter } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (assetIds: string[]) => {
      const responses = await Promise.all(
        assetIds.map((assetId) => assetStorageService.deleteAsset({ workspaceId, assetId })),
      );
      return responses.map((response) => validateApiResponse(response).data);
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      onSuccess: () => {
        addSnackbar({ message: t('assetStorage.assetsDeleted'), variant: 'success' });
        queryClient.invalidateQueries(queryKeyFormatter(ASSET_STORAGE_ROOT_KEY));
      },
      ...options,
    },
  );
};

export const useCreateAssetMutation = (
  options?: UseApiMutationOptions<typeof assetStorageService.createAsset> & {
    disableNotifications?: true;
  },
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (input: AssetCreateInput) => {
      const response = validateApiResponse(
        await assetStorageService.createAsset({ workspaceId, params: input }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        if (options?.disableNotifications === true) {
          return;
        }
        dispatch(formatErrorPayload(error));
      },
      onSuccess: () => {
        if (options?.disableNotifications === true) {
          return;
        }
        addSnackbar({
          message: t('assetStorage.assetAdded'),
          variant: 'success',
        });
      },
      ...options,
    },
  );
};

export const useUpdateAssetMutation = (
  options?: UseApiMutationOptions<typeof assetStorageService.updateAsset>,
) => {
  const dispatch = useDispatch();
  const { workspaceId } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (input: Omit<UpdateAssetInput, 'workspaceId'>) => {
      const response = validateApiResponse(
        await assetStorageService.updateAsset({ workspaceId, ...input }),
      );
      return response.data;
    },
    {
      onError: (error: Error) => {
        dispatch(formatErrorPayload(error));
      },
      ...options,
    },
  );
};

type UseUploadAssetVariables = {
  file: File;
  presignedUrl: string;
  contentType: string;
};

export const useUploadAssetMutation = (
  options?: UseApiMutationOptions<typeof assetStorageService.uploadAsset>,
) => {
  const { t } = useTranslation();
  const { addSnackbar } = useSnackbar();
  const { queryKeyFormatter } = useCurrentWorkspaceQueryKey();

  return useMutation(
    async (variables: UseUploadAssetVariables) => {
      const response = validateApiResponse(await assetStorageService.uploadAsset(variables));
      return response.data;
    },
    {
      onError: () => {
        addSnackbar({ message: t('assetStorage.uploadFailed'), variant: 'danger' });
      },
      onSettled: () => {
        setTimeout(() => {
          queryClient.invalidateQueries(queryKeyFormatter(ASSET_STORAGE_ROOT_KEY));
        }, 2000);
      },
      ...options,
    },
  );
};

export const useListAssetUploadsQuery = (
  assetId: string,
  options?: UseApiQueryOptions<typeof assetStorageService.listAssetUploads>,
) => {
  const dispatch = useDispatch();

  const { queryKeyFormatter, workspaceId } = useCurrentWorkspaceQueryKey();
  return useQuery(
    queryKeyFormatter(ASSET_STORAGE_UPLOADS_ROOT_KEY),
    async () =>
      validateApiResponse(await assetStorageService.listAssetUploads({ workspaceId, assetId }))
        .data,
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};

type WithAssetId = {
  assetId: string;
};

type WithContentType = {
  contentType: string;
};
export const useRequestAssetUploadMutation = (
  options?: UseApiMutationOptions<typeof assetStorageService.requestAssetUpload>,
) => {
  const dispatch = useDispatch();

  const { workspaceId } = useCurrentWorkspaceQueryKey();
  return useMutation(
    async ({ assetId, contentType }: WithAssetId & WithContentType) => {
      const response = validateApiResponse(
        await assetStorageService.requestAssetUpload({
          workspaceId,
          assetId,
          input: {
            contentType,
          },
        }),
      );

      if (response.data === undefined) {
        throw Error('uploadRequest failed: not proceeding');
      }

      return response.data;
    },
    {
      onError: (error) => dispatch(formatErrorPayload(error)),
      ...options,
    },
  );
};
